import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, useMediaQuery } from '@material-ui/core';
import useRootSelector from 'common/hooks/useRootSelector';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { CustomTheme } from 'common/ui/interfaces';
import { useAppDispatch } from 'store/hooks';
import TokenService from 'services/auth/TokenService';
import * as actions from 'memberPortalModules/authentication/state/actions';
import { setUserRole } from 'common/state/userRole/actions';
import MainImg from 'img/icons/main.png';
import { LoginSelection } from 'modules/authentication/components';
import { LoginPage as ClientPortalLoginPage } from 'modules/authentication/containers';
import { LoginPage as MemberPortalLoginPage } from 'memberPortalModules/authentication/containers';
import {
  selectIsUserLoggedIn,
  selectSelectedOrganizationColor,
  selectSignInAsSupportUserData,
} from 'modules/authentication/state/selectors';
import { selectIsMemberPortalUserLoggedIn } from 'memberPortalModules/authentication/state/selectors';
import { UserRole } from 'common/interfaces/common';
import PolicyLinks from './components/PolicyLinks/PolicyLinks';

const useStyles = makeStyles((theme: CustomTheme) => {
  return {
    container: {
      backgroundColor: theme.palette.background.default,
      display: 'flex',
      height: '100%',

      '@media (max-width: 599px)': {
        flexDirection: 'column',
      },
    },
    form: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      width: '50%',
      marginLeft: '64px',

      '@media (max-width: 599px)': {
        width: '100%',
        marginLeft: 0,
        alignItems: 'flex-start',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: '16px',
      },
      '@media (max-width: 399px)': {
        padding: '0 16px',
      },
    },
    banner: {
      height: '100%',
      width: '50%',
      backgroundImage: `url(${MainImg})`,
      backgroundSize: 'cover',

      '@media (max-width: 599px)': {
        width: '100%',
        height: '100%',
      },
    },
    privacyPolicyTitle: {
      lineHeight: '1rem',
    },
    policyButtons: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '16px',
      gap: '36px',
      width: '50%',
      position: 'absolute',
      bottom: 0,
      left: 0,
      '& > div': {
        margin: '0',
      },
      '@media (max-width: 599px)': {
        width: '100%',
        position: 'static',
        gap: '24px',
      },
    },
  };
});

const Main: FC = () => {
  const [loginView, setLoginView] = useState<UserRole | null>(null);
  const loginViewRef = useRef<UserRole | null>(null);
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const isClientPortalUserLogged: boolean = useRootSelector(selectIsUserLoggedIn);
  const isMemberPortalUserLogged: boolean = useRootSelector(selectIsMemberPortalUserLoggedIn);
  const organizationPrimaryColor = useRootSelector(selectSelectedOrganizationColor);
  const signInAsSupportUserData = useRootSelector(selectSignInAsSupportUserData);
  const isUserLogged = isClientPortalUserLogged || isMemberPortalUserLogged;

  useEffect(() => {
    window.addEventListener('popstate', goBack);
    return () => window.removeEventListener('popstate', goBack);
  }, []);

  useEffect(() => {
    loginViewRef.current = loginView;
  }, [loginView]);

  useEffect(() => {
    if (isUserLogged && TokenService.getAccessToken()) {
      if (organizationPrimaryColor) {
        dispatch(actions.resetOrganizationPrimaryColorAction());
      }

      let userData;
      try {
        const tokenData = TokenService.getTokenData(TokenService.getAccessToken());
        userData = JSON.parse(tokenData?.userData);
      } catch (error) {
        userData = null;
      }

      const isMemberPortal = Boolean(userData?.memberPortal);

      dispatch(setUserRole(isMemberPortal ? 'member' : 'client'));
    }
  }, [dispatch, isUserLogged, organizationPrimaryColor]);

  const goBack = e => {
    e.preventDefault();
    if (loginViewRef) {
      setLoginView(null);
    }
  };

  useEffect(() => {
    if (signInAsSupportUserData) {
      setLoginView('client');
    }
  }, [signInAsSupportUserData]);

  switch (loginView) {
    case 'client':
      return <ClientPortalLoginPage setLoginView={setLoginView} />;
    case 'member':
      return <MemberPortalLoginPage setLoginView={setLoginView} />;
    default:
      return (
        <Box className={classes.container}>
          {isMobile && <Box className={classes.banner} />}
          <Box className={classes.form}>
            <LoginSelection onSelect={setLoginView} />
            <Box className={classes.policyButtons}>
              <PolicyLinks />
            </Box>
          </Box>
          {!isMobile && <Box className={classes.banner} />}
        </Box>
      );
  }
};

export default Main;
